<template>
  <div
    class="portable-text"
    :class="{
      'portable-text--medium': medium,
      'portable-text--light': light,
    }"
    v-html="render"
  />
</template>

<script>
// import htm from 'htm'
// import vhtml from 'vhtml'
import { toHTML } from '@portabletext/to-html'

import services from '@/services'

// const html = htm.bind(vhtml)

export default {
  name: 'PortableText',
  props: {
    /** Le texte a afficher */
    blocks: {
      type: Array,
      required: true,
    },
    medium: {
      type: Boolean,
      default: false,
    },
    light: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    mutationObserver: null,
    components: {
      marks: {
        biblio_tooltip: ({ children, value }) => {
          if (value && value.reference && value.reference._ref) {
            return `<span class="tooltip" data-ref=${value.reference._ref}
              >${children}</span
            >`
          }

          return children
        },
        left: ({ children }) => `<div class="text-left">${children}</div>`,
        center: ({ children }) => `<div class="text-center">${children}</div>`,
        right: ({ children }) => `<div class="text-right">${children}</div>`,
        link: ({ children, value }) => {
          if (value.type === 'internal') {
            return `<a data-nuxt-link='${JSON.stringify(
              value.internalLink
            )}'>${children}</a>`
          }

          if (value.type === 'email') {
            return `<a href="mailto:${value.email}">${children}</a>`
          }

          if (value.type === 'phone') {
            return `<a href="tel:${value.phone}">${children}</a>`
          }

          return `<a
            href="${value.url}"
            target="${value.blank ? '_blank' : ''}"
            >${children}</a
          >`
        },
      },
    },
  }),
  computed: {
    render() {
      return toHTML(this.blocks, {
        components: this.components,
      })
    },
  },
  mounted() {
    this.initTooltips()

    const config = { childList: true, subtree: true, attributes: true }
    this.mutationObserver = new MutationObserver(() => {
      this.initInternalLinksSupport()
    })
    this.mutationObserver.observe(this.$el, config)

    this.initInternalLinksSupport()
  },
  beforeDestroy() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
    }
  },
  methods: {
    async initTooltips() {
      const tooltips = this.$el.querySelectorAll('.tooltip')

      if (!tooltips.length === 0) return

      const tippy = await import('tippy.js' /* webpackChunkName: 'tippy' */)
      await import(
        '@/scss/09-libs/_06-tippy.scss' /* webpackChunkName: 'tippy_css' */
      )
      await import(
        'tippy.js/animations/scale.css' /* webpackChunkName: 'tippy_animation_scale_css' */
      )

      for (const tooltip of tooltips) {
        tippy.default(tooltip, {
          theme: 'dark',
          placement: 'top',
          animation: 'scale',
          content: this.$t('loading'),
          arrow: true,
          onShow: (instance) => {
            const ref = instance.reference.getAttribute('data-ref')

            services.sanityService
              .getBiblio({ ref, lang: this.$i18n.locale.slice(0, 2) })
              .then((biblio) => {
                if (!biblio) {
                  instance.setContent(this.$t('request.no-result'))
                  return
                }

                const explication = biblio.explication.find(
                  (e) => e._key === this.$i18n.locale.slice(0, 2)
                )

                if (
                  !explication ||
                  explication.value ||
                  explication.value.length === 0
                ) {
                  instance.setContent(this.$t('request.no-result'))
                  return
                }

                instance.setContent(toHTML(explication.value))
              })
              .catch((err) => {
                instance.setContent(this.$t('request.failed'))
                this.$logError.captureException(err)
              })
          },
        })
      }
    },
    initInternalLinksSupport() {
      const links = this.$el.querySelectorAll('a[data-nuxt-link]')

      for (const link of links) {
        const target = this.$contextSanityPath(
          JSON.parse(link.getAttribute('data-nuxt-link'))
        )

        if (link.href) {
          const href = new URL(link.href)

          if (href.pathname === target) return
        }

        link.addEventListener('click', (e) => {
          e.preventDefault()
          this.$router.push(
            this.$contextSanityPath(
              JSON.parse(link.getAttribute('data-nuxt-link'))
            )
          )
        })

        link.href = target
      }
    },
  },
}
</script>

<style lang="scss">
.portable-text {
  --portable-text-color: inherit;
  --portable-text-margin: calc(var(--spacing) * 2);
  --portable-title-margin: calc(var(--spacing) * 3);

  max-width: rem(720px);
  margin-left: auto;
  margin-right: auto;

  h1,
  h2,
  h3,
  h4,
  h5,
  p,
  a,
  li {
    color: var(--portable-text-color);
  }

  p,
  ul {
    margin-bottom: var(--portable-text-margin);
  }

  p {
    + ul,
    + ol {
      margin-top: calc(var(--portable-text-margin) * -0.5);
    }
  }

  a {
    text-decoration: underline;

    @include on-hover-and-focus {
      text-decoration: none;
    }
  }

  > *:last-child {
    margin-bottom: 0;
  }

  .left-align,
  .center-align,
  .right-align {
    display: block;
  }

  .left-align {
    text-align: left;
  }

  .center-align {
    text-align: center;
  }

  .right-align {
    text-align: right;
  }

  h1,
  h2,
  h3,
  h4,
  h5 {
    @include mq($from: tablet) {
      margin-bottom: var(--portable-title-margin);

      &:not(:first-child) {
        margin-top: var(--portable-title-margin);
      }
    }
  }

  &--medium {
    --portable-title-margin: calc(var(--spacing) * 2);
  }

  &--light {
    --portable-title-margin: calc(var(--spacing) * 1);
  }
}

.tooltip {
  cursor: help;

  &::after {
    content: '';
    background: url('~/assets/img/info.svg') no-repeat;
    background-size: 100% 100%;
    display: inline-block;
    width: rem(14px);
    height: rem(14px);
    margin-left: rem(4px);
    position: relative;
    top: rem(2px);
  }

  &--sun {
    &::after {
      background-image: url('~/assets/img/info-red.svg');
    }
  }

  &.text-color-tertiary {
    --portable-text-color: var(--color-tertiary);
  }

  &.text-color-secondary {
    --portable-text-color: var(--color-secondary);
  }

  &.text-color-yellow {
    --portable-text-color: var(--color-yellow);
  }
}
</style>
